import React from 'react';

/* ============================================================
   Tiny SVG chart primitives. All accept a w/h pair and a color
   (CSS variable or hex). They’re deliberately lightweight — no
   axes, no labels — designed to live inside cards/tiles.
   ============================================================ */

/* ── Sparkline (existing) ── */
export function Sparkline({ values = [4, 6, 5, 8, 7, 9], w = 200, h = 50, color = 'var(--text-primary)' }) {
  // Guard against empty / single-point / non-numeric series: those produce NaN
  // coordinates, which React happily writes into the SVG and the browser then
  // rejects with an "Expected number" console error on every render.
  const vals = (Array.isArray(values) ? values : []).map(Number).filter(Number.isFinite);
  if (vals.length < 2) return <svg width={w} height={h} style={{ display: 'block' }} />;
  const max = Math.max(...vals), min = Math.min(...vals);
  const pts = vals.map((v, i) => {
    const x = (i / (vals.length - 1)) * w;
    const y = h - ((v - min) / (max - min || 1)) * h;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width={w} height={h} style={{ display: 'block' }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth={2}
        strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* ── BarChart (existing — kept for compatibility) ── */
export function BarChart({ values = [3, 5, 2, 7, 4, 6, 8, 3], h = 80, w = 240, color = 'var(--accent-primary)' }) {
  const max = Math.max(...values);
  const barW = (w - values.length * 4) / values.length;
  return (
    <div style={{ width: w, height: h, display: 'flex', alignItems: 'flex-end', gap: 4 }}>
      {values.map((v, i) => (
        <div key={i} style={{
          width: barW, height: `${(v / max) * 100}%`,
          background: color, opacity: 0.85,
          borderTopLeftRadius: 3, borderTopRightRadius: 3,
          transition: 'height 0.5s ease',
        }} />
      ))}
    </div>
  );
}

/* ── Waveform (existing) ── */
export function Waveform({ w = 600, h = 60, played = 0.35 }) {
  const bars = 110;
  const heights = React.useMemo(() => Array.from({ length: bars },
    (_, i) => 0.2 + Math.abs(Math.sin(i * 0.37) * Math.cos(i * 0.13) * Math.sin(i * 0.05 + 1)) * 0.8
  ), []);
  const barW = w / bars - 1;
  const playedIdx = Math.floor(bars * played);
  return (
    <div style={{ width: w, height: h, display: 'flex', alignItems: 'center', gap: 1 }}>
      {heights.map((hh, i) => (
        <div key={i} style={{
          width: barW, height: hh * h,
          background: i < playedIdx ? 'var(--accent-primary)' : 'rgba(255,255,255,0.12)',
          borderRadius: 1,
          transition: 'background 0.15s ease',
        }} />
      ))}
    </div>
  );
}

/* ── Placeholder (existing) ── */
export function Placeholder({ w = 80, h = 80, label = '', round = false, style = {} }) {
  return (
    <div style={{
      width: w, height: h,
      borderRadius: round ? '50%' : 'var(--radius-sm)',
      background: 'var(--accent-glow)',
      border: '1px solid var(--border-color)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600,
      color: 'var(--accent-primary-hover)',
      flexShrink: 0,
      ...style,
    }}>{label}</div>
  );
}

/* ============================================================
   NEW dashboard chart styles — one per module tile
   ============================================================ */

/* 1. AreaSpark — filled-area trend with gradient under the line */
export function AreaSpark({ values = [4,6,5,8,7,9,8], w = 220, h = 48, color = 'var(--accent-primary)' }) {
  if (values.length < 2) return null;
  const max = Math.max(...values), min = Math.min(...values);
  const pad = 2;
  const yRange = h - pad * 2;
  const xs = values.map((_, i) => (i / (values.length - 1)) * w);
  const ys = values.map(v => pad + (1 - (v - min) / (max - min || 1)) * yRange);
  const line = xs.map((x, i) => `${x},${ys[i]}`).join(' ');
  const area = `${xs[0]},${h} ${line} ${xs[xs.length - 1]},${h}`;
  const gid = `area-${Math.random().toString(36).slice(2, 8)}`;
  return (
    <svg width={w} height={h} style={{ display: 'block' }}>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"  stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={area} fill={`url(#${gid})`} />
      <polyline points={line} fill="none" stroke={color} strokeWidth={2}
                strokeLinecap="round" strokeLinejoin="round" />
      {values.map((_, i) => i === values.length - 1 && (
        <circle key={i} cx={xs[i]} cy={ys[i]} r={3} fill={color} stroke="var(--bg-card)" strokeWidth={1.5} />
      ))}
    </svg>
  );
}

/* 2. FunnelBars — horizontal bars that taper, perfect for revival funnels */
export function FunnelBars({
  segments = [
    { l: 'POOL',     v: 2184, c: 'var(--text-tertiary)' },
    { l: 'CONTACTED', v: 312, c: 'var(--info)' },
    { l: 'REPLIED',  v: 44,   c: 'var(--warning)' },
    { l: 'BOOKED',   v: 18,   c: 'var(--success)' }, // demo data only — not a LeadStage
  ],
  w = 220, h = 48,
}) {
  // `max` is 0 when every segment is empty (a brand-new tenant), which makes
  // every bar width NaN — fall back to 1 so the bars just render at zero width.
  const max = Math.max(...segments.map(s => Number(s.v) || 0)) || 1;
  const barH = (h - (segments.length - 1) * 3) / segments.length;
  return (
    <svg width={w} height={h} style={{ display: 'block' }}>
      {segments.map((s, i) => {
        const bw = ((Number(s.v) || 0) / max) * (w - 30);
        const y = i * (barH + 3);
        return (
          <g key={i}>
            <rect x={0} y={y} width={bw} height={barH} fill={s.c} opacity={0.85} rx={2} />
            <text x={bw + 4} y={y + barH * 0.75} fontSize={9} fontFamily="var(--font-mono)" fill="var(--text-tertiary)">
              {(Number(s.v) || 0).toLocaleString()}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/* 3. GaugeArc — semicircular gauge for percentage metrics */
export function GaugeArc({ value = 0.89, w = 220, h = 60, color = 'var(--success)', label }) {
  const cx = w / 2, cy = h - 6, r = Math.min(w / 2 - 4, h - 8);
  const start = Math.PI;
  const end = Math.PI * (1 + Math.max(0, Math.min(1, value)));
  const arc = (a) => `${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`;
  const trackEnd = `${cx + r * Math.cos(2 * Math.PI)},${cy + r * Math.sin(2 * Math.PI)}`;
  const fillEnd = `${cx + r * Math.cos(end)},${cy + r * Math.sin(end)}`;
  const largeArc = value > 0.5 ? 1 : 0;
  return (
    <svg width={w} height={h} style={{ display: 'block' }}>
      <path
        d={`M ${arc(start)} A ${r} ${r} 0 1 1 ${trackEnd}`}
        fill="none" stroke="var(--border-color)" strokeWidth={6} strokeLinecap="round"
      />
      <path
        d={`M ${arc(start)} A ${r} ${r} 0 ${largeArc} 1 ${fillEnd}`}
        fill="none" stroke={color} strokeWidth={6} strokeLinecap="round"
      />
      <text x={cx} y={cy - 6} textAnchor="middle" fontSize={18} fontWeight="700" fill="var(--text-primary)">
        {Math.round(value * 100)}%
      </text>
      {label && (
        <text x={cx} y={cy + 8} textAnchor="middle" fontSize={9}
              fontFamily="var(--font-mono)" fill="var(--text-tertiary)" letterSpacing="0.5">
          {label}
        </text>
      )}
    </svg>
  );
}

/* 4. ScoreHistogram — coloured-zone vertical histogram for QA score distribution */
export function ScoreHistogram({
  buckets = [2,3,5,8,11,14,18,24,30,38,52,68,84,102,118,130,98,52],
  w = 220, h = 48,
  zones = [
    { until: 0.20, color: 'var(--danger)'  },
    { until: 0.45, color: 'var(--warning)' },
    { until: 1.00, color: 'var(--success)' },
  ],
}) {
  const max = Math.max(...buckets);
  const gap = 1;
  const bw = (w - (buckets.length - 1) * gap) / buckets.length;
  return (
    <svg width={w} height={h} style={{ display: 'block' }}>
      {buckets.map((v, i) => {
        const ratio = i / (buckets.length - 1);
        const zone = zones.find(z => ratio <= z.until) || zones[zones.length - 1];
        const bh = (v / max) * h;
        return (
          <rect
            key={i}
            x={i * (bw + gap)}
            y={h - bh}
            width={bw}
            height={bh}
            fill={zone.color}
            opacity={0.85}
            rx={1}
          />
        );
      })}
    </svg>
  );
}

/* 5. RiskDonut — segmented donut showing customer risk split */
export function RiskDonut({
  segments = [
    { l: 'LOW',  v: 7, c: 'var(--success)' },
    { l: 'MED',  v: 2, c: 'var(--warning)' },
    { l: 'HIGH', v: 3, c: 'var(--danger)'  },
  ],
  w = 220, h = 60,
}) {
  const cx = h / 2 + 6, cy = h / 2, r = h / 2 - 6, ir = r - 8;
  const total = segments.reduce((s, x) => s + x.v, 0) || 1;
  let acc = -Math.PI / 2;
  return (
    <svg width={w} height={h} style={{ display: 'block' }}>
      {segments.map((s, i) => {
        const ang = (s.v / total) * Math.PI * 2;
        const a0 = acc, a1 = acc + ang;
        acc = a1;
        const large = ang > Math.PI ? 1 : 0;
        const p0 = `${cx + r * Math.cos(a0)},${cy + r * Math.sin(a0)}`;
        const p1 = `${cx + r * Math.cos(a1)},${cy + r * Math.sin(a1)}`;
        const p2 = `${cx + ir * Math.cos(a1)},${cy + ir * Math.sin(a1)}`;
        const p3 = `${cx + ir * Math.cos(a0)},${cy + ir * Math.sin(a0)}`;
        return (
          <path
            key={i}
            d={`M ${p0} A ${r} ${r} 0 ${large} 1 ${p1} L ${p2} A ${ir} ${ir} 0 ${large} 0 ${p3} Z`}
            fill={s.c}
            opacity={0.9}
          />
        );
      })}
      <text x={cx} y={cy + 3} textAnchor="middle" fontSize={14} fontWeight="700" fill="var(--text-primary)">
        {total}
      </text>

      {/* Legend */}
      {segments.map((s, i) => (
        <g key={i} transform={`translate(${h + 12}, ${10 + i * 14})`}>
          <rect width={8} height={8} fill={s.c} rx={1.5} />
          <text x={14} y={8} fontSize={10} fontFamily="var(--font-mono)" fill="var(--text-secondary)">
            {s.l} · {s.v}
          </text>
        </g>
      ))}
    </svg>
  );
}

/* 6. StackedBars — multi-stage bars (open / weighted / won) for the deal tile */
export function StackedBars({
  groups = [
    { l: 'NEW', open: 3, won: 0 },
    { l: 'QUAL', open: 4, won: 0 },
    { l: 'PROP', open: 5, won: 0 },
    { l: 'WON', open: 0, won: 7 },
    { l: 'LOST',open: 0, won: 0 },
  ],
  w = 220, h = 48,
  colors = { open: 'var(--accent-primary)', won: 'var(--success)' },
}) {
  const max = Math.max(...groups.map(g => g.open + g.won));
  const gap = 4;
  const bw = (w - (groups.length - 1) * gap) / groups.length;
  return (
    <svg width={w} height={h} style={{ display: 'block' }}>
      {groups.map((g, i) => {
        const total = g.open + g.won;
        const totalH = (total / max) * h;
        const openH = (g.open / Math.max(total, 1)) * totalH;
        const wonH  = totalH - openH;
        const x = i * (bw + gap);
        const yOpen = h - totalH;
        const yWon  = h - wonH;
        return (
          <g key={i}>
            {g.open > 0 && (
              <rect x={x} y={yOpen} width={bw} height={openH} fill={colors.open} opacity={0.85} rx={2} />
            )}
            {g.won > 0 && (
              <rect x={x} y={yWon} width={bw} height={wonH} fill={colors.won} opacity={0.9} rx={2} />
            )}
            <text x={x + bw / 2} y={h - 1} textAnchor="middle"
                  fontSize={8} fontFamily="var(--font-mono)" fill="var(--bg-card)">
              {g.l}
            </text>
          </g>
        );
      })}
    </svg>
  );
}


